
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Star, Quote } from 'lucide-react';
import testimonialsData from '@/data/testimonials.json';

interface Testimonial {
  id: string
  name: string
  position: string
  company: string | null
  testimonial: string
  rating: number
  image_url: string | null
  display_order: number
  is_active: boolean
}

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Array of elegant gradient colors
  const cardColors = [
    'bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800',
    'bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800',
    'bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800',
    'bg-gradient-to-br from-amber-600 via-amber-700 to-amber-800',
    'bg-gradient-to-br from-rose-600 via-rose-700 to-rose-800',
    'bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800',
  ];
  
  useEffect(() => {
    // Load static data
    const data = (testimonialsData as Testimonial[])
      .filter(t => t.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setTestimonials(data)
    setLoading(false)
  }, []);
  
  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  if (loading) {
    return (
      <section className="section-padding bg-gray-50/50">
        <div className="container-custom">
          <div className="text-center">Loading testimonials...</div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <section className="section-padding bg-gray-50/50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground font-heading">
              Client Testimonials
            </h2>
            <div className="h-1 w-20 bg-primary mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-muted-foreground">
              No testimonials available yet. Please run the data fetch script to populate content.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Show 3 testimonials at once
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < Math.min(3, testimonials.length); i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push({
        ...testimonials[index],
        colorClass: cardColors[index % cardColors.length]
      });
    }
    return visible;
  };

  const visibleTestimonials = getVisibleTestimonials();

  return (
    <section className="section-padding bg-gray-50/50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground font-heading">
            Client Testimonials
          </h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-muted-foreground">
            Hear what our clients have to say about our services and solutions.
          </p>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          {/* Three Column Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {visibleTestimonials.map((testimonial, index) => (
              <div key={`${testimonial.id}-${currentIndex}-${index}`} className="animate-on-scroll">
                {/* Testimonial Card */}
                <div className={`${testimonial.colorClass} rounded-2xl p-8 text-white shadow-xl relative overflow-hidden min-h-[280px] flex flex-col justify-between`}>
                  {/* Quote Icon */}
                  <Quote className="w-10 h-10 opacity-30 absolute top-6 right-6" />
                  
                  {/* Testimonial Content */}
                  <div className="relative z-10">
                    <p className="text-base md:text-lg leading-relaxed font-medium italic mb-6">
                      "{testimonial.testimonial}"
                    </p>
                    
                    {/* Rating Stars */}
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-white/10 rounded-full"></div>
                  <div className="absolute -top-4 -left-4 w-16 h-16 bg-white/5 rounded-full"></div>
                </div>
                
                {/* Profile Section */}
                <div className="flex items-center mt-6 px-2">
                  <div className="w-14 h-14 rounded-full overflow-hidden border-3 border-white shadow-lg flex-shrink-0 bg-gray-200">
                    <img 
                      src={testimonial.image_url || '/placeholder.svg'} 
                      alt={testimonial.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-foreground">{testimonial.name}</h4>
                    <p className="text-muted-foreground text-sm">
                      {testimonial.position}
                      {testimonial.company && ` • ${testimonial.company}`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation - Only show if more than 3 testimonials */}
          {testimonials.length > 3 && (
            <div className="flex justify-center items-center gap-6">
              <button 
                onClick={prevTestimonial}
                className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-100"
                aria-label="Previous testimonials"
              >
                <ArrowLeft className="w-5 h-5 text-primary" />
              </button>
              
              {/* Dots Indicator */}
              <div className="flex gap-2">
                {Array.from({ length: testimonials.length }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentIndex === index 
                        ? 'bg-primary scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              
              <button 
                onClick={nextTestimonial}
                className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-100"
                aria-label="Next testimonials"
              >
                <ArrowRight className="w-5 h-5 text-primary" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
